import { getClientEnvironmentSnapshot, ClientEnvironmentSnapshot } from '../utils/deviceInfo';

export interface ClientErrorPayload {
  level?: 'error' | 'warn' | 'info';
  type:
    | 'REACT_ERROR'
    | 'UNHANDLED_ERROR'
    | 'PROMISE_REJECTION'
    | 'NETWORK_ERROR'
    | 'CHUNK_LOAD_ERROR'
    | 'VITE_PRELOAD_ERROR'
    | 'RESOURCE_LOAD_ERROR'
    | 'CUSTOM';
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  userId?: number | string | null;
  userEmail?: string | null;
  metadata?: Record<string, any>;
  timestamp?: string;
}

class ClientLogger {
  private queue: ClientErrorPayload[] = [];
  private flushTimer: any = null;
  private recentFingerprints = new Set<string>();
  private currentUser: { id?: number | string; email?: string } | null = null;
  private isInitialized = false;

  constructor() {
    // Automatically clear deduplication set every 60 seconds
    setInterval(() => {
      this.recentFingerprints.clear();
    }, 60000);
  }

  /**
   * Set user context for subsequent error logs.
   */
  public setUser(user: { id?: number | string; email?: string } | null) {
    this.currentUser = user;
  }

  /**
   * Initialize global window error handlers and lifecycle listeners.
   */
  public init() {
    if (this.isInitialized || typeof window === 'undefined') return;
    this.isInitialized = true;

    // 1. Unhandled JS Errors (window.onerror & Resource Load Errors)
    window.addEventListener(
      'error',
      (event: ErrorEvent | Event) => {
        // Handle HTML Element resource failure (e.g. <script>, <link> stylesheet, <img> failed to load)
        if (event.target && event.target !== window && (event.target as HTMLElement).tagName) {
          const target = event.target as HTMLElement;
          const tagName = target.tagName.toLowerCase();
          const src = (target as any).src || (target as any).href || '';

          if (tagName === 'script' || tagName === 'link') {
            const lowerSrc = src.toLowerCase();
            // Ignore benign third-party analytics, tracking, or browser extension scripts blocked by AdBlock / Brave / Pi-hole
            if (
              lowerSrc.includes('googletagmanager.com') ||
              lowerSrc.includes('google-analytics.com') ||
              lowerSrc.includes('analytics.google.com') ||
              lowerSrc.includes('mc.yandex.ru') ||
              lowerSrc.includes('metrika.yandex.ru') ||
              lowerSrc.includes('top-fwz1.mail.ru') ||
              lowerSrc.includes('vk.com/rtrg') ||
              lowerSrc.includes('connect.facebook.net') ||
              lowerSrc.startsWith('chrome-extension://') ||
              lowerSrc.startsWith('moz-extension://') ||
              lowerSrc.startsWith('safari-extension://')
            ) {
              return;
            }

            this.captureError({
              type: 'RESOURCE_LOAD_ERROR',
              level: 'error',
              message: `Сбой загрузки ресурса <${tagName}>: ${src}`,
              metadata: {
                tagName,
                src,
                currentUrl: window.location.href,
              },
            });
          }
          return;
        }

        const errorEvent = event as ErrorEvent;
        // Ignore routine cross-origin resize / script noise and browser extension errors
        if (
          errorEvent.message?.includes('ResizeObserver loop') ||
          errorEvent.message?.includes('Script error.') ||
          errorEvent.message?.includes('WebSocket closed without opened')
        ) {
          return;
        }

        this.captureError({
          type: 'UNHANDLED_ERROR',
          message: errorEvent.message || 'Window Unhandled Error',
          stack: errorEvent.error?.stack || `${errorEvent.filename}:${errorEvent.lineno}:${errorEvent.colno}`,
          metadata: {
            filename: errorEvent.filename,
            lineno: errorEvent.lineno,
            colno: errorEvent.colno,
          },
        });
      },
      true // capture phase for resource loading failures
    );

    // 2. Unhandled Promise Rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      let message = 'Unhandled Promise Rejection';
      let stack = '';

      if (reason instanceof Error) {
        message = reason.message;
        stack = reason.stack || '';
      } else if (typeof reason === 'string') {
        message = reason;
      } else if (typeof reason === 'object' && reason !== null) {
        message = reason.message || JSON.stringify(reason);
      }

      // Ignore canceled axios requests or intentional aborts and third-party extension noise
      if (
        message.includes('canceled') ||
        message.includes('AbortError') ||
        message.includes('WebSocket closed without opened')
      ) {
        return;
      }

      const lowerMsg = message.toLowerCase();
      const isChunkError =
        lowerMsg.includes('importing a module script failed') ||
        lowerMsg.includes('dynamically imported module') ||
        lowerMsg.includes('failed to fetch dynamically imported module');

      this.captureError({
        type: isChunkError ? 'CHUNK_LOAD_ERROR' : 'PROMISE_REJECTION',
        message,
        stack,
        metadata: typeof reason === 'object' ? reason : { rawReason: String(reason) },
      });
    });

    // 3. Flush queue when user leaves or closes window
    window.addEventListener('beforeunload', () => {
      this.flushImmediately();
    });
  }

  /**
   * Capture and queue an error.
   */
  public captureError(payload: Partial<ClientErrorPayload> & { message: string }) {
    try {
      const fingerprint = `${payload.type || 'ERR'}:${payload.message.slice(0, 100)}:${payload.stack?.slice(0, 100) || ''}`;

      // Rate-limit: skip duplicate errors that happened within last minute
      if (this.recentFingerprints.has(fingerprint)) {
        return;
      }
      this.recentFingerprints.add(fingerprint);

      // Snapshot client environment (OS, Browser, Device, Screen, Network)
      let envSnapshot: ClientEnvironmentSnapshot | null = null;
      try {
        envSnapshot = getClientEnvironmentSnapshot();
      } catch (envErr) {
        // ignore snapshot error
      }

      const mergedMetadata = {
        ...(envSnapshot ? { clientEnv: envSnapshot } : {}),
        ...(payload.metadata || {}),
      };

      const entry: ClientErrorPayload = {
        level: payload.level || 'error',
        type: payload.type || 'UNHANDLED_ERROR',
        message: payload.message,
        stack: payload.stack,
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        userId: payload.userId !== undefined ? payload.userId : this.currentUser?.id || null,
        userEmail: payload.userEmail !== undefined ? payload.userEmail : this.currentUser?.email || null,
        metadata: mergedMetadata,
        timestamp: new Date().toISOString(),
      };

      this.queue.push(entry);

      // Schedule flush in 1.5s (batching) or flush immediately if queue size >= 5 or critical chunk/react error
      if (
        this.queue.length >= 5 ||
        payload.type === 'CHUNK_LOAD_ERROR' ||
        payload.type === 'VITE_PRELOAD_ERROR' ||
        payload.type === 'RESOURCE_LOAD_ERROR'
      ) {
        this.flushImmediately();
      } else if (!this.flushTimer) {
        this.flushTimer = setTimeout(() => {
          this.flushImmediately();
        }, 1500);
      }
    } catch (err) {
      // Avoid recursive crash inside logger
      console.error('[ClientLogger Internal Error]', err);
    }
  }

  /**
   * Flush queue to backend endpoint using navigator.sendBeacon or fetch.
   */
  public flushImmediately() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.queue.length === 0) return;

    const itemsToSend = [...this.queue];
    this.queue = [];

    const jsonPayload = JSON.stringify(itemsToSend);
    const endpoint = '/api/logs/client';

    // Priority: navigator.sendBeacon (most reliable during page unload/crashes)
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const blob = new Blob([jsonPayload], { type: 'application/json' });
        const success = navigator.sendBeacon(endpoint, blob);
        if (success) return;
      } catch (beaconErr) {
        // Fallback to fetch below
      }
    }

    // Fallback: standard fetch with keepalive
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: jsonPayload,
      keepalive: true
    }).catch(() => {
      // Silent catch to prevent infinite error propagation
    });
  }
}

export const clientLogger = new ClientLogger();
